import React, { useMemo, useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Alert
} from "@mui/material";
import {
  McTable,
  McButton,
  McTag,
  McPagination
} from "@maersk-global/mds-react-wrapper";
import {
  Warning as WarningIcon,
} from "@mui/icons-material";
import { McIcon } from "@maersk-global/mds-react-wrapper/components-core/mc-icon";
import Papa from "papaparse";
import { saveAs } from "file-saver";


/**
 * ApplicationTable component displays applications in a table format using McTable
 * 
 * @param {Array} applications - Array of applications to display
 * @param {Array} classicChangeApps - Array of classic change applications
 * @param {Array} rapidChangeApps - Array of rapid change applications
 * @param {Array} classicRapidChangeApps - Array of classic+rapid change applications
 * @param {string} title - Table title
 * @returns {JSX.Element} The rendered table component
 */
const ApplicationTable = ({ 
  applications, 
  classicChangeApps = [], 
  rapidChangeApps = [], 
  classicRapidChangeApps = [],
  title
}) => {

  // Define the Maersk font family
  const maerskFontFamily = '"Maersk Text", -apple-system, BlinkMacSystemFont, "Microsoft JhengHei", "Microsoft Yahei", "微软雅黑", STXihei, "华文细黑", sans-serif';

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const recordsPerPage = 25; // Number of records per page

  // Combine all applications (onboarded and not onboarded)
  const getAllApplications = () => {
    // If filtered combined apps are provided, use them directly
    if (applications?.filteredCombinedApps) {
      return applications.filteredCombinedApps;
    }

    // Otherwise, combine all applications as before
    const onboardedApps = [
      ...classicChangeApps,
      ...rapidChangeApps,
      ...classicRapidChangeApps
    ];

    // Get not onboarded apps from applications.overviewCards
    const notOnboardedApps = applications?.overviewCards?.allApplications?.notOnboardedApps || [];
    
    // Transform not onboarded apps to match the structure
    const transformedNotOnboardedApps = notOnboardedApps.map(app => ({
      id: `not-onboarded-${app.code || app.name}`,
      name: app.name,
      code: app.code,
      owner: Array.isArray(app.owners) ? app.owners[0] : (app.owners || ''),
      onboardingStatus: 'Not Onboarded',
      processType: '',
      lastChange: '',
      changesLast3Months: 0,
      channelStatus: '',
      deploymentChannel: '',
      visibilityHealth: false,
      changeProcessType: 'not_onboarded'
    }));

    return [...onboardedApps, ...transformedNotOnboardedApps];
  };

  const currentApplications = getAllApplications();

  // Client-side pagination - calculate total pages and slice data
  const totalPages = Math.ceil(currentApplications.length / recordsPerPage);
  const paginatedApplications = currentApplications.slice((currentPage - 1) * recordsPerPage, currentPage * recordsPerPage);

  // Define table columns for McTable
  const tableColumns = [
    {
      id: "applicationId",
      label: "Application ID",
      sortable: true,
    },
    {
      id: "applicationInstances",
      label: "Application Instances",
      sortable: true,
    },
    {
      id: "owner",
      label: "Owner",
      sortable: true,
    },
    {
      id: "status",
      label: "Status",
      sortable: true,
    },
    {
      id: "processType",
      label: "Process Type",
      sortable: true,
    },
    {
      id: "activity",
      label: "Activity (3M)",
      sortable: true,
    },
    {
      id: "lastChange",
      label: "Last Change",
      sortable: true,
    },
    {
      id: "changeVisibility",
      label: "Change Visibility (3M)",
      sortable: true,
    },
    {
      id: "deploymentChannel",
      label: "Deployment Channel",
      sortable: true,
    }
  ];

  // Transform data for McTable - use paginated data
  const transformedData = useMemo(() => {
    return paginatedApplications.map((app, index) => {
      // Update process type display for classic+rapid
      let processTypeDisplay = app.processType;
      if (app.changeProcessType === 'classic+rapid') {
        processTypeDisplay = app.processType.replace('Classic+Rapid', 'Manual & Automated');
      }
      if (app.changeProcessType === 'classic') {
        processTypeDisplay = app.processType.replace('Classic', 'Manual');
      }
      if (app.changeProcessType === 'rapid') {
        processTypeDisplay = app.processType.replace('Rapid', 'Automated');
      }

      return {
        id: `app-${index}`,
        applicationId: app.code || '',
        applicationInstances: app.name || '',
        owner: app.owner || '',
        status: app.onboardingStatus,
        processType: processTypeDisplay,
        activity: {
          changes: app.changesLast3Months || 0,
          failures: app.failures || 0
        },
        lastChange: app.lastChange || '',
        changeVisibility: app.visibilityHealth,
        deploymentChannel: app.deploymentChannel || '',
        _originalData: app
      };
    });
  }, [paginatedApplications]);

  // Get status tag appearance
  const getStatusAppearance = (status) => {
    switch (status) {
      case 'Onboarded':
        return 'success';
      case 'In Progress':
        return 'warning';
      case 'Pending':
        return 'warning';
      case 'Not Onboarded':
        return 'error';
      default:
        return 'neutral';
    }
  };

  // Handle pagination change
  const handlePageChange = (event) => {
    setCurrentPage(event.detail);
  };

  // Handle download functionality
  const handleDownloadClick = () => {
    const headers = [
      "Application ID",
      "Application Instances", 
      "Owner",
      "Status",
      "Process Type",
      "Activity (3M)",
      "Last Change",
      "Change Visibility (3M)",
      "Deployment Channel"
    ];

    // Prepare data for export with comma-separated owners
    const exportData = currentApplications.map((app) => {
      // Handle owners - if it's an array, join with commas, otherwise use as is
      let ownersString = '';
      if (Array.isArray(app.owners)) {
        ownersString = app.owners.join(', ');
      } else if (app.owner) {
        ownersString = app.owner;
      } else if (app.owners) {
        ownersString = app.owners;
      }

      // Update process type display for export
      let processTypeDisplay = app.processType || '';
      if (app.changeProcessType === 'classic+rapid') {
        processTypeDisplay = processTypeDisplay.replace('Classic+Rapid', 'Manual & Automated');
      }
      if (app.changeProcessType === 'classic') {
        processTypeDisplay = processTypeDisplay.replace('Classic', 'Manual');
      }
      if (app.changeProcessType === 'rapid') {
        processTypeDisplay = processTypeDisplay.replace('Rapid', 'Automated');
      }

      return [
        app.code || '',
        app.name || '',
        ownersString,
        app.onboardingStatus || '',
        processTypeDisplay,
        app.changesLast3Months || 0,
        app.lastChange ? new Date(app.lastChange).toLocaleDateString() : '',
        app.changeProcessType === 'not_onboarded' ? '' : (app.visibilityHealth ? 'Yes' : 'No'),
        app.deploymentChannel === 'green' ? 'Green Channel' : (app.deploymentChannel === 'red' ? 'Red Channel' : '')
      ];
    });

    const csv = Papa.unparse({
      fields: headers,
      data: exportData,
    });
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    saveAs(blob, "Applications.csv");
  };

  // Reset to first page when applications change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [currentApplications.length]);

  return (
    <Card sx={{ 
      mb: 3, 
      mt: 2,
      border: '1px solid #d9d9d9', 
      boxShadow: 'none',
      borderRadius: '10px'
    }}>
      <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
        <Box sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" component="div" sx={{ fontFamily: maerskFontFamily }}>
              {title}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="body2" sx={{ color: '#0077b2', fontFamily: maerskFontFamily }}>
                Total: {currentApplications.length}
              </Typography>
              <McButton
                label="Download"
                appearance="primary"
                variant="filled"
                fit="small"
                click={handleDownloadClick}
              >
                <span slot="trailingicon">
                  <mc-icon class="my-icon" icon="tray-arrow-down" color="white"></mc-icon>
                </span>
              </McButton>
            </Box>
          </Box>

          <McTable
            data={transformedData}
            columns={tableColumns}
            datakey="id"
            headersticky
            fit="small"
            customstyles={`
              .mds-table {
                max-height: 70vh !important;
              }
            `}
            sortdefaultcolumnid="deploymentChannel"
            sortdefaultdirection="descending"
          >
          {transformedData?.length === 0 && (
            <div slot="state" style={{ textAlign: "center", padding: "2rem", fontSize: "1.2rem" }}>
              No Applications Found
            </div>
          )}
          
          {transformedData?.map((row) => (
            <React.Fragment key={row.id}>

              {/* Application ID Cell */}
              <div slot={`${row.id}_applicationId`}>
                <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: maerskFontFamily }}>
                  {row.applicationId}
                </Typography>
              </div>

              {/* Application Instances Cell */}
              <div slot={`${row.id}_applicationInstances`}>
                <Typography variant="body2" sx={{ fontFamily: maerskFontFamily }}>
                  {row.applicationInstances}
                </Typography>
              </div>

              {/* Owner Cell */}
              <div slot={`${row.id}_owner`}>
                <Typography variant="body2" sx={{ fontFamily: maerskFontFamily }}>{row.owner}</Typography>
              </div>

              {/* Status Cell */}
              <div slot={`${row.id}_status`}>
                <McTag 
                  label={row.status} 
                  appearance={getStatusAppearance(row.status)}
                  size="small"
                />
              </div>

              {/* Process Type Cell */}
              <div slot={`${row.id}_processType`}>
                <Typography variant="body2" sx={{ fontFamily: maerskFontFamily }}>{row.processType}</Typography>
              </div>

              {/* Activity Cell */}
              <div slot={`${row.id}_activity`}>
                {row._originalData.changeProcessType === 'not_onboarded' ? (
                  <Typography variant="body2" sx={{ fontFamily: maerskFontFamily }}> </Typography>
                ) : (
                  <Box>
                    <Typography variant="body2" sx={{ mb: 0.5, fontFamily: maerskFontFamily }}>
                      📊 {row.activity.changes} changes
                    </Typography>
                    {row.activity.failures > 0 && (
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <WarningIcon fontSize="small" color="warning" sx={{ mr: 0.5, fontSize: 14 }} />
                        <Typography variant="caption" color="warning.main" sx={{ fontFamily: maerskFontFamily }}>
                          {row.activity.failures} failures
                        </Typography>
                      </Box>
                    )}
                  </Box>
                )}
              </div>

              {/* Last Change Cell */}
              <div slot={`${row.id}_lastChange`}>
                <Typography variant="body2" sx={{ fontFamily: maerskFontFamily }}>
                  {row.lastChange === '' ? '' : new Date(row.lastChange).toLocaleDateString()}
                </Typography>
              </div>

              {/* Change Visibility Cell */}
              <div slot={`${row.id}_changeVisibility`}>
                {row._originalData.changeProcessType === 'not_onboarded' ? (
                  <Typography variant="body2" sx={{ fontFamily: maerskFontFamily, textAlign: 'center' }}> </Typography>
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <McIcon 
                      icon={row.changeVisibility ? "check-circle" : "times-circle"}
                      color={row.changeVisibility ? "#4caf50" : "#f44336"}
                      size={20}
                    />
                  </Box>
                )}
              </div>

              {/* Deployment Channel Cell */}
              <div slot={`${row.id}_deploymentChannel`}>
                {row.deploymentChannel === '' ? (
                  <Typography variant="body2" sx={{ fontFamily: maerskFontFamily }}> </Typography>
                ) : (
                  <McTag 
                    label={row.deploymentChannel === 'green' ? 'Green Channel' : 'Red Channel'} 
                    appearance={row.deploymentChannel === 'green' ? 'success' : 'error'}
                    size="small"
                  />
                )}
              </div>
            </React.Fragment>
          ))}
        </McTable>

        {/* Pagination Component */}
        {currentApplications.length > recordsPerPage && (
          <Box sx={{ paddingTop: '1rem', display: 'flex', justifyContent: 'center' }}>
            <McPagination
              currentpage={currentPage}
              totalpages={totalPages}
              pagechange={handlePageChange}
            />
          </Box>
        )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default ApplicationTable;
